import { roundCurrency } from '@bill-alps/backend/shared/finance/money';
import { billVatBuckets } from '@bill-alps/backend/shared/finance/vatBuckets';

/**
 * Pure computation of the Swiss ESTV VAT declaration boxes from bills.
 *
 * Method:
 * - 'convenues' (agreed considerations, the default authorization): bills
 *   counted by issueDate, statuses sent/paid/overdue.
 * - 'recues' (received considerations): bills counted by paidDate, status paid.
 *
 * Amounts are converted to CHF with the official BAZG monthly average rate
 * of the bill's relevant month; bills whose rate is unknown are excluded and
 * reported. Per-line vatRate is authoritative (bill-level vatRate is written
 * as 0 by the editor when items carry individual rates).
 */

export type VatMethod = 'convenues' | 'recues';

export interface VatBillItem {
  vatRate: number;
  quantity: number;
  unitPrice: number;
}

export interface VatBill {
  invoiceNumber: string;
  status: string;
  issueDate: number;
  paidDate?: number;
  currency: string;
  vatAmount: number;
  discount?: number;
  shippingCosts?: number;
  items: VatBillItem[];
}

export interface RateEntry {
  yearMonth: string;
  currency: string;
  ratePerUnit: number;
}

export interface VatDeclarationInput {
  bills: VatBill[];
  rates: RateEntry[];
  from: number; // ms, inclusive
  to: number; // ms, inclusive
  method: VatMethod;
}

export type VatBoxCode = '302' | '312' | '342' | '303' | '313' | '343';

export interface VatRateLine {
  code: VatBoxCode;
  ratePct: number;
  baseChf: number;
  taxChf: number;
}

export interface NonStandardRateLine {
  ratePct: number;
  baseChf: number;
  taxChf: number;
}

export interface AppliedFxRate {
  currency: string;
  yearMonth: string;
  ratePerUnit: number;
}

export interface ExcludedBill {
  invoiceNumber: string;
  currency: string;
  yearMonth: string;
}

export interface VatDeclarationResult {
  box200: number;
  zeroRatedToAllocate: number;
  box289: number;
  box299: number;
  rateLines: VatRateLine[];
  nonStandardRates: NonStandardRateLine[];
  box399: number;
  invoicedVatChf: number;
  appliedRates: AppliedFxRate[];
  excludedBills: ExcludedBill[];
  billCount: number;
}

/** Current (2024+) and legacy (pre-2024) standard rates → form box codes. */
const RATE_TO_BOX = new Map<number, VatBoxCode>([
  [8.1, '302'],
  [2.6, '312'],
  [3.8, '342'],
  [7.7, '303'],
  [2.5, '313'],
  [3.7, '343'],
]);

const CONVENUES_STATUSES = new Set(['sent', 'paid', 'overdue']);

export function tsToYearMonth(ts: number): string {
  const d = new Date(ts);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function relevantDate(bill: VatBill, method: VatMethod): number | undefined {
  return method === 'convenues' ? bill.issueDate : bill.paidDate;
}

function filterBills(
  bills: VatBill[],
  from: number,
  to: number,
  method: VatMethod,
): VatBill[] {
  return bills.filter((bill) => {
    if (method === 'convenues') {
      if (!CONVENUES_STATUSES.has(bill.status)) return false;
    } else if (bill.status !== 'paid' || bill.paidDate === undefined) {
      return false;
    }
    const date = relevantDate(bill, method);
    return date !== undefined && date >= from && date <= to;
  });
}

/**
 * Year-months of in-period non-CHF bills — the months whose BAZG rates
 * must be fetched before computing the declaration.
 */
export function periodMonths(
  bills: VatBill[],
  from: number,
  to: number,
  method: VatMethod,
): string[] {
  const months = new Set<string>();
  for (const bill of filterBills(bills, from, to, method)) {
    if (bill.currency !== 'CHF') {
      months.add(tsToYearMonth(relevantDate(bill, method) as number));
    }
  }
  return [...months].sort();
}

export type PeriodPreset = 'Q1' | 'Q2' | 'Q3' | 'Q4' | 'S1' | 'S2' | 'YEAR';

/** ISO date range (inclusive) for a declaration period preset. */
export function presetRange(
  year: number,
  preset: PeriodPreset,
): { from: string; to: string } {
  const ranges: Record<PeriodPreset, [string, string]> = {
    Q1: [`${year}-01-01`, `${year}-03-31`],
    Q2: [`${year}-04-01`, `${year}-06-30`],
    Q3: [`${year}-07-01`, `${year}-09-30`],
    Q4: [`${year}-10-01`, `${year}-12-31`],
    S1: [`${year}-01-01`, `${year}-06-30`],
    S2: [`${year}-07-01`, `${year}-12-31`],
    YEAR: [`${year}-01-01`, `${year}-12-31`],
  };
  const [from, to] = ranges[preset];
  return { from, to };
}

/** Per-bill VAT-exclusive bases by rate — shared with the ledger and the
 * invoice totals (`@bill-alps/backend/shared/finance/vatBuckets`). */
function billBuckets(bill: VatBill): Map<number, number> {
  return billVatBuckets(bill);
}

export function computeVatDeclaration(
  input: VatDeclarationInput,
): VatDeclarationResult {
  const included = filterBills(input.bills, input.from, input.to, input.method);

  const rateByKey = new Map<string, number>();
  for (const rate of input.rates) {
    rateByKey.set(`${rate.currency}|${rate.yearMonth}`, rate.ratePerUnit);
  }

  const basesByRate = new Map<number, number>();
  const appliedByKey = new Map<string, AppliedFxRate>();
  const excludedBills: ExcludedBill[] = [];
  let invoicedVatChf = 0;
  let billCount = 0;

  for (const bill of included) {
    const yearMonth = tsToYearMonth(relevantDate(bill, input.method) as number);
    let fx = 1;
    if (bill.currency !== 'CHF') {
      const key = `${bill.currency}|${yearMonth}`;
      const rate = rateByKey.get(key);
      if (rate === undefined) {
        excludedBills.push({
          invoiceNumber: bill.invoiceNumber,
          currency: bill.currency,
          yearMonth,
        });
        continue;
      }
      fx = rate;
      appliedByKey.set(key, {
        currency: bill.currency,
        yearMonth,
        ratePerUnit: rate,
      });
    }

    billCount += 1;
    invoicedVatChf += roundCurrency(bill.vatAmount * fx);
    for (const [rate, base] of billBuckets(bill)) {
      const baseChf = roundCurrency(base * fx);
      basesByRate.set(rate, (basesByRate.get(rate) ?? 0) + baseChf);
    }
  }

  let box200 = 0;
  let zeroRatedToAllocate = 0;
  const rateLines: VatRateLine[] = [];
  const nonStandardRates: NonStandardRateLine[] = [];
  let box399 = 0;

  for (const [ratePct, rawBase] of basesByRate) {
    const baseChf = roundCurrency(rawBase);
    box200 += baseChf;
    if (ratePct === 0) {
      zeroRatedToAllocate += baseChf;
      continue;
    }
    const taxChf = roundCurrency((baseChf * ratePct) / 100);
    box399 += taxChf;
    const code = RATE_TO_BOX.get(ratePct);
    if (code) {
      rateLines.push({ code, ratePct, baseChf, taxChf });
    } else {
      nonStandardRates.push({ ratePct, baseChf, taxChf });
    }
  }

  const boxOrder: VatBoxCode[] = ['302', '312', '342', '303', '313', '343'];
  rateLines.sort((a, b) => boxOrder.indexOf(a.code) - boxOrder.indexOf(b.code));
  nonStandardRates.sort((a, b) => b.ratePct - a.ratePct);

  box200 = roundCurrency(box200);
  zeroRatedToAllocate = roundCurrency(zeroRatedToAllocate);
  const box289 = zeroRatedToAllocate;
  const box299 = roundCurrency(box200 - box289);
  box399 = roundCurrency(box399);
  invoicedVatChf = roundCurrency(invoicedVatChf);

  return {
    box200,
    zeroRatedToAllocate,
    box289,
    box299,
    rateLines,
    nonStandardRates,
    box399,
    invoicedVatChf,
    appliedRates: [...appliedByKey.values()].sort((a, b) =>
      `${a.currency}${a.yearMonth}`.localeCompare(
        `${b.currency}${b.yearMonth}`,
      ),
    ),
    excludedBills,
    billCount,
  };
}
